/**
 * A builder's public profile — what a business sees before inviting them.
 * Stats are counted by Twasta from finished orders (never typed in); bank
 * details are never part of this view (GET /build/builders/:tenantId).
 * Doc: docs/marketplace_get_it_built.md
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { fmtDate, money, mktError } from '@/api/marketplace'
import { getBuilder, type PublicBuilder } from '@/api/marketplaceBuild'
import { useAuth } from '@/auth/AuthContext'
import MarketplaceShell, { AppTile } from '../MarketplaceShell'
import { Avatar, Loading } from './builderUi'

type Tab = 'overview' | 'reviews' | 'history' | 'apps'

function stars(n: number): string {
  const full = Math.max(0, Math.min(5, Math.round(n)))
  return '★'.repeat(full) + '☆'.repeat(5 - full)
}

export default function BuilderProfilePage() {
  const { tenantId = '' } = useParams()
  const { user, authStatus } = useAuth()
  const [b, setB] = useState<PublicBuilder | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('overview')

  const load = useCallback(async () => {
    try {
      setB(await getBuilder(tenantId))
      setError(null)
    } catch (e) {
      setError(mktError(e, "We couldn't load this builder's profile.").message)
    }
  }, [tenantId])
  useEffect(() => { load() }, [load])

  if (error) return <MarketplaceShell><div className="mkt-wrap" style={{ padding: '32px 20px' }}><div role="alert" className="mkt-banner bad"><div><strong>Something went wrong</strong>{error}</div><button type="button" className="btn btn-secondary" onClick={load}>Try again</button></div></div></MarketplaceShell>
  if (!b) return <MarketplaceShell><Loading text="Loading the profile…" /></MarketplaceShell>

  const own = authStatus === 'authed' && !!user && user.tenant_id === b.tenant_id
  const s = b.stats
  const statTiles: [string, string][] = [
    [s.rating != null ? `${s.rating} ★` : '—', s.reviews ? `Rating · ${s.reviews} review${s.reviews === 1 ? '' : 's'}` : 'No ratings yet'],
    [String(s.completed), 'Orders finished'],
    [s.on_time_pct != null ? `${s.on_time_pct}%` : '—', 'Finished on time'],
    [String(s.apps_live), 'Apps in use by clients'],
  ]
  const tabs: [Tab, string][] = [['overview', 'Overview'], ['reviews', `Reviews (${b.reviews.length})`], ['history', `Work history (${b.history.length})`], ['apps', `Marketplace apps (${b.listings.length})`]]

  return (
    <MarketplaceShell>
      <div className="mkt-wrap mkt-col" style={{ padding: '20px 20px 64px', gap: 16 }}>
        {own && (
          <div className="mkt-banner plain">
            <div>This is your public profile — what businesses see.</div>
            <Link to="/marketplace/builder?tab=profile" className="btn btn-secondary btn-sm">Edit profile</Link>
          </div>
        )}

        <section className="mkt-panel mkt-row" style={{ gap: 24, alignItems: 'flex-start', padding: 26 }}>
          <Avatar text={b.initials} size={104} available={b.available} />
          <div className="mkt-col" style={{ flex: '1 1 420px', minWidth: 0, gap: 8 }}>
            <div className="mkt-row" style={{ gap: 10 }}>
              <h1 style={{ fontSize: 30 }}>{b.name}</h1>
              <span className={`mkt-pill ${b.available ? 'ok' : ''}`}>{b.available ? 'Available for new work' : 'Not taking new work'}</span>
            </div>
            {b.person && b.person !== b.name && <span className="mkt-muted">{b.person}</span>}
            {b.headline && <div style={{ fontSize: 18, fontWeight: 600 }}>{b.headline}</div>}
            <div className="mkt-muted" style={{ fontSize: 14 }}>{[b.location, b.rate_note].filter(Boolean).join(' · ')}</div>
          </div>
          {!own && authStatus === 'authed' && (
            <div className="mkt-col" style={{ gap: 8 }}>
              <Link to="/marketplace/build/new" className="btn btn-primary">Ask for an estimate</Link>
              <span className="mkt-faint" style={{ maxWidth: 220 }}>Post a request and invite {b.name} from its last step.</span>
            </div>
          )}
          <div className="mkt-kpis" style={{ flex: '1 1 100%' }}>
            {statTiles.map(([v, l]) => (
              <div key={l} className="mkt-kpi" style={{ background: 'var(--color-bg)' }}><strong>{v}</strong><span className="mkt-muted">{l}</span></div>
            ))}
          </div>
        </section>

        <div role="tablist" aria-label="Profile sections" className="mkt-tabs">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={`mkt-tab${tab === id ? ' on' : ''}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>

        <div className="mkt-split">
          <div className="mkt-main" role="tabpanel">
            {tab === 'overview' && (
              <>
                <section className="mkt-panel mkt-col">
                  <h2>About</h2>
                  {b.about ? <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{b.about}</p> : <span className="mkt-muted">{b.name} hasn't written about their work yet.</span>}
                </section>
                <section className="mkt-panel mkt-col">
                  <h2>Skills</h2>
                  {b.skills.length ? <div className="mkt-row" style={{ gap: 8 }}>{b.skills.map((k) => <span key={k} className="mkt-pill info">{k}</span>)}</div> : <span className="mkt-muted">No skills listed yet.</span>}
                </section>
              </>
            )}

            {tab === 'reviews' && (
              <section className="mkt-panel mkt-col" style={{ gap: 14 }}>
                {s.rating != null && (
                  <div className="mkt-row" style={{ gap: 14 }}>
                    <strong style={{ fontSize: 40 }}>{s.rating}</strong>
                    <div className="mkt-col" style={{ gap: 0 }}><span aria-hidden="true" style={{ color: 'var(--color-warning)', fontSize: 18 }}>{stars(s.rating)}</span><span className="mkt-muted">{s.reviews} review{s.reviews === 1 ? '' : 's'} from clients</span></div>
                  </div>
                )}
                {b.reviews.length === 0 && <span className="mkt-muted">No reviews yet. Reviews come from clients when they sign off an order.</span>}
                {b.reviews.map((r, i) => (
                  <article key={i} className="mkt-col" style={{ gap: 4, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                    <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                      <strong>{r.title || 'Order'}</strong>
                      <span style={{ color: 'var(--color-warning)' }} aria-label={`${r.stars} out of 5 stars`}>{stars(r.stars)}</span>
                    </div>
                    <span className="mkt-faint">{fmtDate(r.at)}{r.amount_minor ? ` · ${money(r.amount_minor, r.currency)}` : ''}</span>
                    {r.text && <p style={{ margin: 0 }}>“{r.text}”</p>}
                  </article>
                ))}
              </section>
            )}

            {tab === 'history' && (
              <section className="mkt-panel mkt-col">
                {b.history.length === 0 ? <span className="mkt-muted">No finished orders yet.</span> : (
                  <div className="mkt-scroll">
                    <table className="mkt-table" style={{ minWidth: 460 }}>
                      <thead><tr><th scope="col">Order</th><th scope="col">Finished</th><th scope="col" className="num">Value</th></tr></thead>
                      <tbody>{b.history.map((h, i) => <tr key={i}><td><strong>{h.title}</strong></td><td>{h.completed_at ? fmtDate(h.completed_at) : '—'}</td><td className="num">{money(h.price_minor, h.currency)}</td></tr>)}</tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {tab === 'apps' && (
              b.listings.length === 0
                ? <div className="mkt-empty"><strong>No Marketplace apps yet</strong><span className="mkt-muted">{b.name} doesn't sell a ready-made app in the Marketplace right now.</span></div>
                : (
                  <div className="mkt-grid">
                    {b.listings.map((l) => (
                      <Link key={l.slug} to={`/marketplace/app/${encodeURIComponent(l.slug)}`} className="mkt-card">
                        <div className="mkt-row" style={{ flexWrap: 'nowrap' }}>
                          <AppTile text={l.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()} />
                          <strong style={{ fontSize: 17 }}>{l.name}</strong>
                        </div>
                        <span className="mkt-muted" style={{ fontSize: 14 }}>See it in the Marketplace →</span>
                      </Link>
                    ))}
                  </div>
                )
            )}
          </div>

          <aside className="mkt-aside">
            <div className="mkt-panel mkt-col" style={{ gap: 8, padding: 18, fontSize: 14 }}>
              <strong>Working with {b.name}</strong>
              <ul className="mkt-muted" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <li>They build in their own Twasta account; you get a private copy in yours, with its own database.</li>
                <li>You pay Twasta stage by stage; Twasta pays the builder only after you approve each stage.</li>
                <li>Once installed, you pay a monthly price to use it, like any Marketplace app.</li>
              </ul>
            </div>
            {b.listings.length > 0 && (
              <div className="mkt-panel mkt-col" style={{ gap: 8, padding: 18 }}>
                <strong>Apps {b.name} sells in the Marketplace</strong>
                {b.listings.slice(0, 4).map((l) => <Link key={l.slug} to={`/marketplace/app/${encodeURIComponent(l.slug)}`}>{l.name}</Link>)}
                <span className="mkt-faint">Want one as it is? Install it. Want it changed for you? Ask {b.name} for an estimate.</span>
              </div>
            )}
          </aside>
        </div>
      </div>
    </MarketplaceShell>
  )
}
